import React, { ReactNode, useEffect, useState } from 'react';
import { Button, Icon } from '@iwoca/orion';
import { t } from '@lingui/macro';
import clsx from 'clsx';
import CopyToClipboard from 'react-copy-to-clipboard';
import { Tooltip } from 'react-tooltip';
import { v4 } from 'uuid';
import styles from './CopyableText.module.css';

type TCopyToClipboardOptions = {
  debug?: boolean | undefined;
  message?: string | undefined;
  format?: string | undefined; // MIME type
};

type TProps = {
  text: string;
  onCopy?: (text: string) => void;
  options?: TCopyToClipboardOptions | undefined;
  children?: ReactNode;
  copyButtonComponent?: ReactNode;
  textClassName?: string;
  buttonClassName?: string;
  className?: string;
  disabled?: boolean;
  testId?: string;
};

export const CopyableText = ({
  text,
  onCopy,
  options,
  children,
  copyButtonComponent,
  buttonClassName,
  textClassName,
  className,
  disabled = false,
  testId,
}: TProps) => {
  const [labelText, setLabelText] = useState(t`Copy`);
  const [hasCopied, setHasCopied] = useState(false);
  const [copyId] = useState(v4());

  useEffect(() => {
    let timeOut: NodeJS.Timeout | undefined;

    if (hasCopied) {
      clearTimeout(timeOut);
      setLabelText(t`Copied!`);
    } else {
      timeOut = setTimeout(() => {
        setLabelText(t`Copy`);
      }, 2000);
    }

    return () => {
      clearTimeout(timeOut);
    };
  }, [hasCopied]);

  const handleOnCopy = (text: string) => {
    onCopy?.(text);
    setHasCopied(true);
  };

  return (
    <div className={clsx(styles.copyableText, className)}>
      {children || <p className={clsx(styles.text, textClassName)}>{text}</p>}
      {!disabled && (
        <CopyToClipboard text={text} onCopy={handleOnCopy} options={options}>
          {copyButtonComponent || (
            <Button
              variant="tertiary"
              size="small"
              data-testid={testId || 'copy-button'}
              data-tooltip-id={copyId}
              onMouseLeave={() => setHasCopied(false)}
              onMouseEnter={() => setLabelText(t`Copy`)}
              className={buttonClassName}
            >
              <Icon
                height={20}
                width={20}
                icon="copyOutline"
                className={styles.copyIcon}
              />

              {/* TODO: Replace with Orion's upcoming dynamic tooltip when its built and uninstall react-tooltip  */}
              <Tooltip
                className={styles.tooltip}
                id={copyId}
                opacity={1}
                content={labelText}
                openEvents={{ focus: false, mouseenter: true }}
                place="bottom"
                border="1px solid var(--color-structure-80)"
              />
            </Button>
          )}
        </CopyToClipboard>
      )}
    </div>
  );
};
